import { engineComponent } from "neo-common";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { Symbols } from "../Symbols";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { StageController } from "../../Components/StageController";
import { WinAnimator } from "../WinAnimator";
import { GameManager } from "../../Managers/GameManager";
import { PlayList } from "../../Components/Sounds/PlayList";
import { ModuleManager } from "../../Managers/ModuleManager";
import { Tweens } from "../../Components/Effects/Tweens";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { ProtonElement } from "../../Components/Elements/Graphics/ProtonElement";
import { DataManager } from "../../Managers/DataManager";
import { BetManager } from "../../Managers/BetManager";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    private _controller: BaseController;

    private _wasFinished: boolean = false;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _paylinesSelector: SwitchElement;
    private _symbols: Symbols;
    private _machine: Machine;
    private _winAnimator: WinAnimator;
    private _noWinContainer: ContainerElement;
    private _winContainer: ContainerElement;

    private _teasing: SwitchElement;

    private _background: ImageElement;
    private _logo: ImageElement;
    private _reelsBackground: ImageElement;
    private _treeLeft: ImageElement;
    private _treeRight: ImageElement;
    private _grass: ImageElement;
    private _leaf: ProtonElement;

    private _reelsBackgroundY: number = 0;
    private _winAnimatorY: number = 0;
    private _logoY: number = 0;
    private _paylinesY: number = 0;
    private _treeLeftX: number = 0;
    private _treeRightX: number = 0;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        StageController.baseStageContainer = this;
    }

    create(node: Element): void {
        super.create(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                    this._paylines.reset();
                    this._paylinesY = this._paylines.y;
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                    this._winAnimatorY = this._winAnimator.y;
                }

                if (key === "PaylinesSelector" && this.childs[key] instanceof SwitchElement) {
                    this._paylinesSelector = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "WinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._winContainer = this.childs[key];
                }

                if (key === "Teasing" && this.childs[key] instanceof SwitchElement) {
                    this._teasing = this.childs[key];
                }

                if (key === "Logo" && this.childs[key] instanceof ImageElement) {
                    this._logo = this.childs[key];
                    this._logoY = this._logo.y;
                }

                if (key === "ReelsBackground" && this.childs[key] instanceof ImageElement) {
                    this._reelsBackground = this.childs[key];
                    this._reelsBackgroundY = this._reelsBackground.y;
                }

                if (key === "TreeLeft" && this.childs[key] instanceof ImageElement) {
                    this._treeLeft = this.childs[key];
                    this._treeLeftX = this._treeLeft.x;
                }

                if (key === "TreeRight" && this.childs[key] instanceof ImageElement) {
                    this._treeRight = this.childs[key];
                    this._treeRightX = this._treeRight.x;
                }

                if (key === "Leaf" && this.childs[key] instanceof ProtonElement) {
                    this._leaf = this.childs[key];
                }

                if (key === "Background" && this.childs[key] instanceof ImageElement) {
                    this._background = this.childs[key];
                }

                if (key === "Grass" && this.childs[key] instanceof ImageElement) {
                    this._grass = this.childs[key];
                }
            }

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }
            }
        }
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            false,
                            this._teasing,
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this._noWinContainer,
                                this._winContainer,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                            this._winAnimator.showInitExpandWilds(GameManager.baseResponse.wins);
                        }

                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }
    }

    initIntro(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;

        if (GameManager.gameResponse.totalWin > 0 || ModuleManager.freespinsEnabled()) {
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.loop("SND_BaseLoop");
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.goToCase("NORMAL");
        }

        this._winAnimator.reset(true);
    }

    spin(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.goToCase("NORMAL");
        }

        this._machine.spinStart(this._winAnimator.reset(false));
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    machineStopped(): void {
        this._wasFinished = false;

        this._controller.machineStopped();

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;

        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay || ModuleManager.freespinsEnabled()) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        StageController.baseStage = true;

        if (long) {
            if (this._background != null) {
                Tweens.alphaTween(this._background, {
                    alphas: [0, 1],
                    tweenDuration: 60,
                });
            }

            if (this.childs["BackgroundStrip"] != null) {
                Tweens.alphaTween(this.childs["BackgroundStrip"], {
                    alphas: [0, 1],
                    tweenDuration: 60,
                });
            }

            if (this.childs["BackgroundTransitionStrip"] != null) {
                this.childs["BackgroundTransitionStrip"].visible = true;
            }

            if (this._grass != null) {
                Tweens.alphaTween(this._grass, {
                    alphas: [0, 1],
                    tweenDuration: 60,
                });
            }

            if (this._winAnimator != null) {
                this._winAnimator.reset(true);
                this._winAnimator.y = -StageController.Height;
                Tweens.positionTween(this._winAnimator, {
                    tweenX: [this._winAnimator.x, this._winAnimator.x],
                    tweenY: [-StageController.Height, this._winAnimatorY],
                    tweenDuration: 60,
                    tweenDelay: 90,
                });
            }

            if (this._paylines != null) {
                this._paylines.y = -StageController.Height;
                Tweens.positionTween(this._paylines, {
                    tweenX: [this._paylines.x, this._paylines.x],
                    tweenY: [-StageController.Height, this._paylinesY],
                    tweenDuration: 60,
                    tweenDelay: 90,
                });
            }

            if (this._logo != null) {
                this._logo.y = -StageController.Height;
                Tweens.positionTween(this._logo, {
                    tweenX: [this._logo.x, this._logo.x],
                    tweenY: [-StageController.Height, this._logoY],
                    tweenDuration: 60,
                    tweenDelay: 90,
                });
            }

            if (this._reelsBackground != null) {
                this._reelsBackground.y = -StageController.Height;
                Tweens.positionTween(this._reelsBackground, {
                    tweenX: [this._reelsBackground.x, this._reelsBackground.x],
                    tweenY: [-StageController.Height, this._reelsBackgroundY],
                    tweenDuration: 60,
                    tweenDelay: 90,
                });
            }

            if (this._treeLeft != null) {
                this._treeLeft.x = this._treeLeft.x - this._treeLeft.width;
                Tweens.positionTween(this._treeLeft, {
                    tweenX: [this._treeLeft.x - this._treeLeft.width, this._treeLeftX],
                    tweenY: [this._treeLeft.y, this._treeLeft.y],
                    tweenDuration: 60,
                    tweenDelay: 90,
                });
            }

            if (this._treeRight != null) {
                this._treeRight.x = this._treeRight.x + this._treeRight.width;
                Tweens.positionTween(this._treeRight, {
                    tweenX: [this._treeRight.x + this._treeRight.width, this._treeRightX],
                    tweenY: [this._treeRight.y, this._treeRight.y],
                    tweenDuration: 60,
                    tweenDelay: 90,
                });
            }
            this._introDelay.start(150);
        } else {
            this.introFinished();
        }
    }

    introFinished(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;

        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseLoop");
        }

        if (this._leaf != null) {
            this._leaf.stop();
            this._leaf.start();
        }

        this._controller.introFinished();
    }

    outro(): void {
        StageController.baseStage = true;

        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecapLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.goToCase("NORMAL");
        }

        if (ModuleManager.freespinsActive()) {
            PlayList.play("SND_FSIntro");

            if (this._winAnimator != null) {
                this._winAnimator.reset(true);
                Tweens.positionTween(this._winAnimator, {
                    tweenX: [this._winAnimator.x, this._winAnimator.x],
                    tweenY: [this._winAnimator.y, -StageController.Height],
                    tweenDuration: 60,
                });
            }

            if (this._paylines != null) {
                Tweens.positionTween(this._paylines, {
                    tweenX: [this._paylines.x, this._paylines.x],
                    tweenY: [this._paylines.y, -StageController.Height],
                    tweenDuration: 60,
                });
            }

            if (this._logo != null) {
                Tweens.positionTween(this._logo, {
                    tweenX: [this._logo.x, this._logo.x],
                    tweenY: [this._logo.y, -StageController.Height],
                    tweenDuration: 60,
                });
            }

            if (this._reelsBackground != null) {
                Tweens.positionTween(this._reelsBackground, {
                    tweenX: [this._reelsBackground.x, this._reelsBackground.x],
                    tweenY: [this._reelsBackground.y, -StageController.Height],
                    tweenDuration: 60,
                });
            }

            if (this._treeLeft != null) {
                Tweens.positionTween(this._treeLeft, {
                    tweenX: [this._treeLeft.x, this._treeLeft.x - this._treeLeft.width],
                    tweenY: [this._treeLeft.y, this._treeLeft.y],
                    tweenDuration: 60,
                });
            }

            if (this._treeRight != null) {
                Tweens.positionTween(this._treeRight, {
                    tweenX: [this._treeRight.x, this._treeRight.x + this._treeRight.width],
                    tweenY: [this._treeRight.y, this._treeRight.y],
                    tweenDuration: 60,
                });
            }

            if (this._grass != null) {
                Tweens.alphaTween(this._grass, {
                    alphas: [1, 0],
                    tweenDuration: 60,
                });
            }

            if (this._leaf != null) {
                this._leaf.stop();
            }

            this._outroDelay.start(70);
        } else {
            const inHighWins = this._winAnimator.reset(true);

            if (this._wasFinished) {
                this.outroFinished();
            } else {
                if (inHighWins) {
                    this._outroDelay.start(90);
                } else {
                    this._outroDelay.start(60);
                }
            }
        }
    }

    outroFinished(): void {
        StageController.baseStage = true;

        if (this._leaf != null) {
            this._leaf.stop();
        }

        if (ModuleManager.freespinsActive()) {
            if (this.childs["BackgroundTransitionStrip"] != null) {
                this.childs["BackgroundTransitionStrip"].visible = false;
            }
        }

        this._winAnimator.reset(true);

        this._controller.outroFinished();
    }

    selectPayline(): void {
        if (this._paylinesSelector != null) {
            this._paylinesSelector.goToCase("SELECT_" + BetManager.PaylineCount);
        }
    }

    updatePaylines(): void {
        this._paylines.setPaylines();
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecapLoop");
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/